import React, { useState } from 'react';
import { KeyCenter } from '../utils/keyCenterRegions';
import { midiToName } from '../utils';

/**
 * Popover for choosing a key center: a mode, then a key — including which
 * spelling of a black key, since G♭ and F♯ are the same pitch but not the same
 * thing on a chart, and which one is right depends on the song.
 *
 * One picker serves both places a key center is set — the chart's global key in
 * the header, and a local key center starting at a chord — so the two can never
 * drift apart.
 *
 * Positioned `fixed` from the rect of whatever opened it, clear of the chart
 * body, which scrolls and clips — the same approach the batch-review dialog
 * takes with its anchor.
 *
 * Built from divs rather than buttons: App.css styles every `button` as a
 * raised pill with its own height and position, which a menu this small would
 * have to fight property by property.
 */

/** The two names for each black key, by pitch class. */
const SHARP_NAMES: { [pitchClass: number]: string } = {
  1: 'C♯', 3: 'D♯', 6: 'F♯', 8: 'G♯', 10: 'A♯',
};
const FLAT_NAMES: { [pitchClass: number]: string } = {
  1: 'D♭', 3: 'E♭', 6: 'G♭', 8: 'A♭', 10: 'B♭',
};

const NATURALS = [
  { pitchClass: 0, name: 'C' }, { pitchClass: 2, name: 'D' }, { pitchClass: 4, name: 'E' },
  { pitchClass: 5, name: 'F' }, { pitchClass: 7, name: 'G' }, { pitchClass: 9, name: 'A' },
  { pitchClass: 11, name: 'B' },
];
const BLACK_KEYS = [1, 3, 6, 8, 10];

/**
 * The keys sit on one grid of fourteen equal half-columns, laid out like the
 * keyboard: each natural takes two, and each black key takes the two either
 * side of the line between its neighbours, so C♯ and D♭ are centred exactly
 * between C and D. The widths are fixed rather than taken from the labels, so
 * every key is in the same place whichever spelling or case it is drawn in.
 */
const HALF_COLUMN = 0.95; // dvw
const GRID_COLUMNS = NATURALS.length * 2;
/** The first half-column of a key's cell (grid lines count from 1). */
const startColumn = (pitchClass: number) => {
  const natural = NATURALS.findIndex(n => n.pitchClass === pitchClass);
  // A black key starts on the second half of the natural below it.
  return natural !== -1 ? natural * 2 + 1 : NATURALS.findIndex(n => n.pitchClass === pitchClass - 1) * 2 + 2;
};

/**
 * A key center as text: "E♭" for a major key, "a" or "c♯" for a minor one.
 *
 * Case carries the mode, the way it does in figured harmony and in Roman
 * numerals everywhere else in the app: a capital letter is major, a lower-case
 * one is minor. It is shorter than an "m" suffix and reads at the size these
 * labels are drawn.
 *
 * Uses the spelling stored on the key center when there is one. Without it —
 * a key center from before a spelling was chosen, or one a future auto layer
 * produced — it falls back to the app's own note naming, which spells a tonic
 * from its own key signature.
 */
export function keyCenterName(value: KeyCenter): string {
  let base: string;
  if (value.spelling === 'sharp' && SHARP_NAMES[value.pitchClass] !== undefined) {
    base = SHARP_NAMES[value.pitchClass];
  } else if (value.spelling === 'flat' && FLAT_NAMES[value.pitchClass] !== undefined) {
    base = FLAT_NAMES[value.pitchClass];
  } else {
    base = midiToName(value.pitchClass, false, value.pitchClass, value.mode);
  }
  // Only the letter has a case; ♯ and ♭ come through untouched.
  return value.mode === 1 ? base.toLowerCase() : base;
}

/** A key's mode colour, the one the Key Center panel's lock and Major/Minor
 * button take: yellow for major, blue for minor. */
export const keyCenterColor = (mode: number) => (mode === 1 ? 'rgb(150, 194, 255)' : 'rgb(255, 246, 142)');

/** How much larger a minor key's letter is drawn than the text around it */
const MINOR_LETTER_SCALE = '1.3em';

/**
 * A key's name as drawn: a minor key's lower-case letter enlarged to stand as
 * tall as a major key's capital, so the two read the same size, while the
 * accidental after it is drawn the same in either mode. line-height 0 keeps
 * the larger letter from making its line taller.
 */
export const KeyName: React.FC<{ name: string; minor: boolean }> = ({ name, minor }) => (
  minor
    ? <><span style={{ fontSize: MINOR_LETTER_SCALE, lineHeight: 0 }}>{name.charAt(0)}</span>{name.slice(1)}</>
    : <>{name}</>
);

/** A key center's name as drawn, from keyCenterName. */
export const keyCenterLabel = (value: KeyCenter) => <KeyName name={keyCenterName(value)} minor={value.mode === 1} />;

/** Where the popover hangs from: the bottom edge and centre of its opener.
 * With `top`, it stands on the opener's top edge instead, for an opener near
 * the foot of the screen (the MIDI player). */
export type PickerAnchor = { bottom: number; centerX: number; top?: number };

export type KeyCenterPickerProps = {
  /** The key center currently in force, highlighted in the grid. */
  value: KeyCenter | null;
  /**
   * In force by a "use global key" marker: that choice is highlighted, and
   * `value` is the global key it gives, if there is one.
   */
  usesGlobal?: boolean;
  anchor: PickerAnchor;
  /**
   * `keepOpen` is set when the change came from the Major/Minor tabs rather
   * than from picking a key: switching mode applies straight away, and the
   * picker stays up so the choice can be seen and carried on from.
   */
  onPick: (keyCenter: KeyCenter, keepOpen?: boolean) => void;
  /**
   * "Use global key": the global key from here on, whatever it is, ending any
   * local key in force; the chords before keep theirs. A marker that stays.
   */
  onUseGlobal?: () => void;
  /**
   * "Clear": the key change set here goes, and the key before it carries on;
   * on the global key itself, no global key. Offered only where a key is set.
   */
  onClear?: () => void;
  onDismiss: () => void;
};

const KeyCenterPicker: React.FC<KeyCenterPickerProps> = ({
  value, anchor, usesGlobal, onPick, onUseGlobal, onClear, onDismiss,
}) => {
  // The mode being browsed. Not yet a choice: it decides how the keys below are
  // labelled, and travels with whichever one is clicked.
  const [mode, setMode] = useState<0 | 1>(value === null ? 0 : value.mode);

  // The highlight follows the note, not the mode, so switching Major/Minor
  // leaves the key you are on selected.
  const isCurrent = (pitchClass: number, spelling?: 'sharp' | 'flat') => {
    if (value === null) return false;
    if (value.pitchClass !== pitchClass) return false;
    // A natural has one name, so its spelling is never set and never compared.
    if (spelling === undefined) return true;
    // A key with no spelling of its own (the MIDI analysis's) is the one its
    // name shows
    if (value.spelling === undefined) return /[♯#]/.test(keyCenterName(value)) === (spelling === 'sharp');
    return value.spelling === spelling;
  };

  const cell = (pitchClass: number, label: string, row: number, spelling?: 'sharp' | 'flat') => {
    const current = isCurrent(pitchClass, spelling);
    return (
      <div
        key={`${pitchClass}-${spelling === undefined ? 'natural' : spelling}`}
        onClick={(e) => { e.stopPropagation(); onPick({ pitchClass, mode, spelling }); }}
        onMouseEnter={(e) => {
          if (!current) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
        }}
        onMouseLeave={(e) => {
          if (!current) e.currentTarget.style.backgroundColor = 'transparent';
        }}
        style={{
          gridRow: row,
          gridColumn: `${startColumn(pitchClass)} / span 2`,
          // A hair in from each side, so neighbouring highlights never touch.
          margin: '0 0.05dvw',
          padding: '0.05dvw 0',
          borderRadius: '0.25dvw',
          cursor: 'pointer',
          textAlign: 'center',
          color: current ? '#fff' : 'rgba(255,255,255,0.8)',
          backgroundColor: current
            ? (mode === 0 ? 'rgb(90,90,0)' : 'rgb(50,50,140)')
            : 'transparent',
        }}
      >
        {/* Case says the mode, as it does on the chart: C is C major, c is C
            minor. The highlighted tab above says the same thing, so the grid
            never needs an "m" on every cell. */}
        <KeyName name={mode === 1 ? label.toLowerCase() : label} minor={mode === 1} />
      </div>
    );
  };

  /**
   * Switching mode takes effect on the spot when a key is already set — C major
   * becomes C minor as you click Minor, rather than arming a change that is
   * lost the moment the picker is dismissed. With nothing set there is nothing
   * to switch, so it only decides how the keys below are labelled.
   */
  const pickMode = (tabMode: 0 | 1) => {
    setMode(tabMode);
    if (value !== null && value.mode !== tabMode) onPick({ ...value, mode: tabMode }, true);
  };

  const modeTab = (tabMode: 0 | 1, label: string) => (
    <div
      onClick={(e) => { e.stopPropagation(); pickMode(tabMode); }}
      style={{
        flex: 1,
        padding: '0.05dvw 0.4dvw',
        borderRadius: '0.25dvw',
        cursor: 'pointer',
        textAlign: 'center',
        color: mode === tabMode ? '#fff' : 'rgba(255,255,255,0.55)',
        backgroundColor: mode === tabMode
          ? (tabMode === 0 ? 'rgb(90,90,0)' : 'rgb(50,50,140)')
          : 'transparent',
      }}
    >
      {label}
    </div>
  );

  // Kept inside the viewport: the badge that opens this can sit near either
  // edge of the screen once the chart is wide. Its width is the grid's plus
  // the padding either side, converted from dvw to the anchor's pixels.
  const halfWidth = ((GRID_COLUMNS * HALF_COLUMN + 0.6) / 100) * window.innerWidth / 2;
  const left = Math.min(Math.max(anchor.centerX, halfWidth + 4), window.innerWidth - halfWidth - 4);

  return (
    <>
      {/* Backdrop takes the click that closes the popover. */}
      <div
        data-actionable="true"
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1999 }}
        onClick={onDismiss}
      />
      <div
        data-actionable="true"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed',
          ...(anchor.top !== undefined
            ? { bottom: window.innerHeight - anchor.top + 6 }
            : { top: anchor.bottom + 6 }),
          left,
          transform: 'translateX(-50%)',
          backgroundColor: '#1a1a1a',
          border: '1px solid rgba(255, 255, 255, 0.15)',
          borderRadius: '0.4dvw',
          padding: '0.3dvw',
          zIndex: 2000,
          boxShadow: '0 0.2dvw 0.6dvw rgba(0, 0, 0, 0.5)',
          whiteSpace: 'nowrap',
          // The chart's own font, so a key center is spelled the way every
          // other note and accidental in the app is drawn.
          fontFamily: 'Bravura Text',
          fontWeight: 100,
          fontSize: '1.1dvw',
          lineHeight: 1.25,
          color: 'rgba(255,255,255,0.85)',
        }}
      >
        <div style={{ display: 'flex', gap: '0.2dvw', paddingBottom: '0.15dvw' }}>
          {modeTab(0, 'Major')}
          {modeTab(1, 'Minor')}
        </div>

        {/* Sharps on top, naturals in the middle, flats below — both spellings
            of every black key are on screen at once, so choosing G♭ over F♯ is
            one click rather than a mode to go find. */}
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${GRID_COLUMNS}, ${HALF_COLUMN}dvw)`, rowGap: '0.05dvw' }}>
          {BLACK_KEYS.map(pitchClass => cell(pitchClass, SHARP_NAMES[pitchClass], 1, 'sharp'))}
          {NATURALS.map(natural => cell(natural.pitchClass, natural.name, 2))}
          {BLACK_KEYS.map(pitchClass => cell(pitchClass, FLAT_NAMES[pitchClass], 3, 'flat'))}
        </div>

        {/* "Use global key" is a choice like the keys above, so it is a toggle,
            highlighted while in force, and always centred. "Clear" is an action,
            not a choice: an ✕ at the right, named on hover, there or not
            without moving the toggle. */}
        {(onUseGlobal !== undefined || onClear !== undefined) && (
          <div style={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '1.3dvw',
            marginTop: '0.25dvw',
            paddingTop: '0.2dvw',
            borderTop: '1px solid rgba(255,255,255,0.15)',
            fontSize: '0.85dvw',
          }}>
            {onUseGlobal !== undefined && (
              <div
                title="The global key from here on, ending any key change before it"
                onClick={(e) => { e.stopPropagation(); onUseGlobal(); }}
                onMouseEnter={(e) => {
                  if (!usesGlobal) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
                }}
                onMouseLeave={(e) => {
                  if (!usesGlobal) e.currentTarget.style.backgroundColor = 'transparent';
                }}
                style={{
                  padding: '0.05dvw 0.6dvw',
                  borderRadius: '0.25dvw',
                  border: `1px solid ${usesGlobal ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.25)'}`,
                  cursor: 'pointer',
                  color: usesGlobal ? '#fff' : 'rgba(255,255,255,0.7)',
                  // Highlighted as the grid highlights the key it gives
                  backgroundColor: usesGlobal
                    ? (value === null ? 'rgba(255,255,255,0.18)' : (value.mode === 1 ? 'rgb(50,50,140)' : 'rgb(90,90,0)'))
                    : 'transparent',
                }}
              >
                use global key
              </div>
            )}
            {onClear !== undefined && (
              <div
                title="Clear"
                onClick={(e) => { e.stopPropagation(); onClear(); }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#fff';
                  e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = 'rgba(255,255,255,0.55)';
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
                style={{
                  position: 'absolute',
                  right: 0,
                  top: '50%',
                  transform: 'translateY(-35%)',
                  padding: '0 0.25dvw',
                  borderRadius: '0.25dvw',
                  cursor: 'pointer',
                  color: 'rgba(255,255,255,0.55)',
                }}
              >
                {'\u2715'}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default KeyCenterPicker;
